import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhMalwareApi } from 'src/api/mindtime/wazuhMalwareApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhMalwareCategoriesData,
  WazuhMalwareDetectionItem,
  WazuhMalwareDetectionsQuery,
  WazuhMalwareEndpointItem,
  WazuhMalwareSummaryData,
} from 'src/types/mindtime/wazuhMalware';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type MalwareFacets = {
  types: string[];
};

export type MalwareFilters = {
  severityBand?: string;
  malwareType?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type MalwareState = {
  loading: boolean;
  summary: WazuhMalwareSummaryData | null;
  items: WazuhPagedData<WazuhMalwareDetectionItem> | null;
  endpoints: WazuhPagedData<WazuhMalwareEndpointItem> | null;
  categories: WazuhMalwareCategoriesData | null;
  facets: MalwareFacets;
};

type BuildMalwareQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: MalwareFilters;
  page?: number;
  pageSize?: number;
};

const emptyFacets = (): MalwareFacets => ({
  types: [],
});

const emptyState = (): MalwareState => ({
  loading: true,
  summary: null,
  items: null,
  endpoints: null,
  categories: null,
  facets: emptyFacets(),
});

const mergeFacets = (
  items: WazuhMalwareDetectionItem[],
  previous: MalwareFacets,
  categoryKeys: string[] = []
): MalwareFacets => {
  const types = new Set(previous.types);
  categoryKeys.forEach((key) => {
    if (key) types.add(key);
  });
  items.forEach((item) => {
    if (item.malwareType) types.add(item.malwareType);
  });
  return {
    types: Array.from(types).sort((a, b) => a.localeCompare(b)),
  };
};

const buildMalwareQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  selectedEndpointId,
  page,
  pageSize,
}: BuildMalwareQueryInput & { selectedEndpointId?: string | null }): WazuhMalwareDetectionsQuery => {
  const { severityBand, malwareType, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(malwareType ? { malwareType } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useMalwareExport = (search = '', filters: MalwareFilters = {}) => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode } = useEdrDataGate('malware-detection');
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    if (guardEdrPreviewAction(isPreviewMode, 'previewExportBlocked')) return;
    setExporting(true);
    mindtimeWazuhMalwareApi
      .export(
        buildMalwareQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          selectedEndpointId,
        })
      )
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'malware_detections_export.xlsx';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useMalwareExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters, selectedEndpointId, isPreviewMode]);

  return { exporting, handleExport };
};

export const useMalwareData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: MalwareFilters = {}
): MalwareState => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('malware-detection');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'malware-detection');
  const canSummary = canWidget('summary');
  const canCategories = canWidget('categoriesChart');
  const canList = canWidget('list');
  const canInfectedEndpoints = canWidget('infectedEndpoints');
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<MalwareState>(emptyState);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        resellerId: scope.resellerId,
        startDate: scope.startDate,
        endDate: scope.endDate,
        isReseller,
        organizationId: filters.organizationId || '',
      }),
    [scope.resellerId, scope.startDate, scope.endDate, isReseller, filters.organizationId]
  );

  const { severityBand, malwareType, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId || '';

  useEffect(() => {
    setState((prev) => ({ ...prev, facets: emptyFacets() }));
  }, [scopeKey]);

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as MalwareState);
      return undefined;
    }

    if (!canSummary && !canCategories) {
      setState((prev) => ({ ...prev, summary: null, categories: null }));
      return undefined;
    }

    let cancelled = false;

    const effectiveScope = buildEdrApiScope({
      scope,
      profileOrgId,
      isReseller,
      pageOrganizationId: organizationId || undefined,
    });

    const fetches: Promise<void>[] = [];

    if (canSummary) {
      fetches.push(
        mindtimeWazuhMalwareApi.getSummary(effectiveScope).then((summary) => {
          if (!cancelled) setState((prev) => ({ ...prev, summary }));
        }),
      );
    } else {
      setState((prev) => ({ ...prev, summary: null }));
    }

    if (canCategories) {
      fetches.push(
        mindtimeWazuhMalwareApi.getCategoriesChart(effectiveScope).then((categories) => {
          if (!cancelled) {
            setState((prev) => ({
              ...prev,
              categories,
              facets: mergeFacets([], prev.facets, Object.keys(categories?.categories ?? {})),
            }));
          }
        }),
      );
    } else {
      setState((prev) => ({ ...prev, categories: null }));
    }

    Promise.all(fetches).catch((error) => {
      if (!cancelled) {
        logger(error, 'error', 'useMalwareData.summary');
        setState((prev) => ({
          ...prev,
          summary: canSummary ? null : prev.summary,
          categories: canCategories ? null : prev.categories,
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    organizationId,
    isPreviewMode,
    wait,
    getPreviewState,
    canSummary,
    canCategories,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canList) {
      setState((prev) => ({ ...prev, loading: false, items: null }));
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    const listParams = buildMalwareQuery({
      scope,
      profileOrgId,
      isReseller,
      search,
      filters,
      page,
      pageSize,
      selectedEndpointId,
    });

    mindtimeWazuhMalwareApi
      .getDetections(listParams)
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
          facets: mergeFacets(items?.items ?? [], prev.facets),
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useMalwareData.detections');
          setState((prev) => ({ ...prev, loading: false, items: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    severityBand,
    malwareType,
    wazuhAgentId,
    organizationId,
    isPreviewMode,
    skipLive,
    canList,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canInfectedEndpoints) {
      setState((prev) => ({ ...prev, endpoints: null }));
      return undefined;
    }

    let cancelled = false;

    const listParams = buildMalwareQuery({
      scope,
      profileOrgId,
      isReseller,
      search,
      filters,
      page: 0,
      pageSize,
      selectedEndpointId,
    });

    mindtimeWazuhMalwareApi
      .listEndpoints(listParams)
      .then((endpoints) => {
        if (!cancelled) setState((prev) => ({ ...prev, endpoints }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useMalwareData.endpoints');
          setState((prev) => ({ ...prev, endpoints: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    pageSize,
    search,
    severityBand,
    malwareType,
    wazuhAgentId,
    organizationId,
    isPreviewMode,
    skipLive,
    canInfectedEndpoints,
  ]);

  return state;
};
